import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { ChevronDown, ChevronUp, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { shouldIgnoreEvent } from '@/features/keyboard-shortcuts/keyboardUtils';
import type { SanitizedEmail } from '@/lib/emailHtmlSanitizer';
import { getTheme } from '@/lib/theme/themes';
import { useThemeStore } from '@/stores/themeStore';
import { splitQuotedContent } from '../utils/quotedContent';
import type { ParsedEmailContent } from '../utils/quotedContent';

interface MessageBodyProps {
  sanitizedHtml: SanitizedEmail | null;
  minHeight?: string;
  contentWrapperClassName?: string;
  contentClassName?: string;
  expandQuotedByDefault?: boolean;
}

const DEFAULT_MIN_HEIGHT = 'min-h-[100px]';
const DEFAULT_CONTENT_WRAPPER = 'w-full';

// Fit-to-width bounds. A desktop-width marketing email (the ubiquitous 600px
// table) lands at ~0.65 on a phone, which is the same reduction iOS Mail
// applies. The floor stops a single pathological layout (a 2000px table, a
// nowrap data grid) from shrinking body text into illegibility — past it the
// frame pans horizontally instead.
const MIN_FIT_SCALE = 0.5;
const MAX_LAYOUT_WIDTH = 1600;
// Widening can legitimately need a second look: handing the document more room
// can switch off its own `@media (max-width: …)` rules and reveal a wider
// desktop layout underneath. Bounded so a self-contradicting stylesheet can't
// spin.
const MAX_WIDEN_PASSES = 4;
// Defence in depth against a message whose own CSS still makes its content
// height depend on the frame height. Legitimate growth comes from images and
// fonts settling — a few dozen at the very most — so a longer run of increases
// is a feedback loop, and the frame freezes rather than growing without bound.
const MAX_HEIGHT_GROWTHS = 40;

const BASE_IFRAME_STYLES = `
  html, body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #1a1a1a;
    word-wrap: break-word;
    overflow-wrap: break-word;
    -webkit-text-size-adjust: 100%;
  }
  body {
    overflow: hidden;
  }
  pre {
    white-space: pre-wrap;
    overflow-x: auto;
  }
  /* Keep an oversized image from dictating the whole message's fit scale: it
     shrinks to the column instead, so the layout tables decide the scale.
     height:auto only where it cannot misfire — an image that declares a width
     needs it to keep its aspect ratio once max-width bites, and one with no
     height attribute is already auto. An image sized ONLY by a height
     attribute keeps that height. */
  img {
    max-width: 100%;
  }
  img[width], img:not([height]) {
    height: auto;
  }
  a {
    color: #1a73e8;
  }
  /* Matches only when the frame is rendered dark, which happens only for a
     message that declares dark-mode support (see frameColorScheme). */
  @media (prefers-color-scheme: dark) {
    html, body {
      background: transparent;
      color: #e0e0e0;
    }
    a {
      color: #8ab4f8;
    }
  }
`;

function buildSrcdoc(
  styles: string,
  body: string,
  bodyAttributes: string,
  extraStyles?: string,
): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${BASE_IFRAME_STYLES}</style>
${extraStyles ? `<style>${extraStyles}</style>` : ''}
${styles}
</head>
<body${bodyAttributes ? ` ${bodyAttributes}` : ''}>${body}</body>
</html>`;
}

// width/height/transform are driven imperatively by measure() — they are
// deliberately absent here so React's style diffing never clobbers a measured
// layout on an unrelated re-render. colorScheme is per-message (below).
const IFRAME_STYLE: CSSProperties = {
  border: 'none',
  width: '100%',
  display: 'block',
  overflow: 'hidden',
  transformOrigin: 'top left',
  position: 'absolute',
  top: 0,
  left: 0,
};

interface EmailIframeProps {
  styles: string;
  body: string;
  bodyAttributes: string;
  supportsDarkMode: boolean;
  dimmed?: boolean;
}

/**
 * The scheme a message is painted in. The iframe element's `color-scheme` is
 * what `prefers-color-scheme` resolves to inside the frame, so this is the one
 * switch for both our base styles and the message's own dark-mode CSS.
 *
 * A message that doesn't declare dark support is always painted light, like
 * Apple Mail does — its colours were designed for a light canvas. Under a dark
 * app theme that canvas must be opaque white, or the sender's dark text and
 * any transparent areas would sit on the app's dark surface.
 */
function frameColorScheme(appMode: 'light' | 'dark', supportsDarkMode: boolean) {
  const scheme: 'light' | 'dark' = supportsDarkMode ? appMode : 'light';
  const lightCanvasOnDarkApp = scheme === 'light' && appMode === 'dark';
  return {
    scheme,
    canvasStyles: lightCanvasOnDarkApp ? 'html { background: #ffffff; }' : '',
  };
}

/**
 * Renders one email body in an isolated iframe, sized to fit.
 *
 * The iframe is a WIDTH-LOCKED, height-auto viewport: the parent reads
 * scrollHeight and grows the element so the message scrolls as part of the
 * page. That left horizontal overflow with nowhere to go — a fixed-width
 * (600px) marketing layout in a 390px phone frame was simply clipped by
 * `body { overflow: hidden }`, and the app's own viewport meta sets
 * `user-scalable=no`, so the reader couldn't even pinch to see the rest.
 *
 * So measure instead: give the document the width it actually wants, then
 * scale the whole frame down to the width we have (transform, not zoom — the
 * document's own layout must not depend on the scale, or measurement feeds
 * back on itself). Anything still too wide at the floor scale pans
 * horizontally, and a toggle switches between fit and actual size.
 */
function EmailIframe({ styles, body, bodyAttributes, supportsDarkMode, dimmed }: EmailIframeProps) {
  const appMode = useThemeStore((s) => getTheme(s.previewThemeId ?? s.themeId).mode);
  const { scheme, canvasStyles } = frameColorScheme(appMode, supportsDarkMode);
  const iframeStyle = useMemo<CSSProperties>(
    () => ({ ...IFRAME_STYLE, colorScheme: scheme }),
    [scheme],
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);

  const bodyObserverRef = useRef<ResizeObserver | null>(null);
  const wrapObserverRef = useRef<ResizeObserver | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const keydownCleanupRef = useRef<(() => void) | null>(null);

  // Layout bookkeeping lives in refs: measure() must be able to run from a
  // ResizeObserver without re-rendering, and a re-render must never reset it.
  const containerWidthRef = useRef(0);
  const layoutWidthRef = useRef(0);
  const widenPassesRef = useRef(0);
  const fitRef = useRef(true);
  const appliedHeightRef = useRef(0);
  const heightGrowthsRef = useRef(0);

  const [fitToWidth, setFitToWidth] = useState(true);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(function run() {
    const iframe = iframeRef.current;
    const wrap = wrapRef.current;
    const sizer = sizerRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !wrap || !sizer || !doc?.body) return;

    const container = Math.round(wrap.clientWidth);
    // An inactive split pane stays mounted at zero width; measuring there would
    // bake a nonsense scale into a message before it is ever shown.
    if (container <= 0) return;

    if (container !== containerWidthRef.current) {
      containerWidthRef.current = container;
      layoutWidthRef.current = container;
      widenPassesRef.current = 0;
      heightGrowthsRef.current = 0;
      appliedHeightRef.current = 0;
      iframe.style.width = `${container}px`;
      iframe.style.transform = '';
    }

    const layout = layoutWidthRef.current || container;
    // scrollWidth reports the full scrollable area even under overflow:hidden,
    // which is what makes the clipped-away width observable at all.
    const natural = Math.max(doc.documentElement.scrollWidth, doc.body.scrollWidth);

    if (
      natural > layout + 1 &&
      widenPassesRef.current < MAX_WIDEN_PASSES &&
      layout < MAX_LAYOUT_WIDTH
    ) {
      widenPassesRef.current += 1;
      const widened = Math.min(Math.ceil(natural), MAX_LAYOUT_WIDTH);
      layoutWidthRef.current = widened;
      iframe.style.width = `${widened}px`;
      // Re-measure once the browser has laid the document out at the new width.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(run);
      return;
    }

    const finalLayout = layoutWidthRef.current || container;
    const height = doc.documentElement.scrollHeight;
    if (height <= 0) return;

    if (height > appliedHeightRef.current + 1) {
      heightGrowthsRef.current += 1;
      if (heightGrowthsRef.current > MAX_HEIGHT_GROWTHS) {
        if (heightGrowthsRef.current === MAX_HEIGHT_GROWTHS + 1) {
          console.warn('[MessageBody] content height kept growing; froze the frame to break a feedback loop');
        }
        return;
      }
    }
    appliedHeightRef.current = height;

    const needed = finalLayout > container ? container / finalLayout : 1;
    const scale = fitRef.current ? Math.max(MIN_FIT_SCALE, Math.min(1, needed)) : 1;

    // Escape hatch for the two cases the widening pass cannot resolve: a
    // document wider than MAX_LAYOUT_WIDTH, and a stylesheet whose width
    // oscillates as its own media queries switch on and off. Rather than clip
    // silently — the whole bug being fixed here — let the frame pan itself.
    doc.body.style.overflowX = natural > finalLayout + 1 ? 'auto' : '';

    iframe.style.height = `${height}px`;
    iframe.style.transform = scale === 1 ? '' : `scale(${scale})`;
    // The transform is purely visual — the iframe's layout box stays
    // finalLayout wide — so a sizer carries the SCALED footprint and owns the
    // horizontal scroll range.
    sizer.style.width = `${Math.ceil(finalLayout * scale)}px`;
    sizer.style.height = `${Math.ceil(height * scale)}px`;

    setOverflows(needed < 0.995);
  }, []);

  const toggleFit = useCallback(() => {
    fitRef.current = !fitRef.current;
    setFitToWidth(fitRef.current);
    if (!fitRef.current && wrapRef.current) {
      wrapRef.current.scrollLeft = 0;
    }
    measure();
  }, [measure]);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;

    // A reload replaces the document: re-derive the layout width from the
    // container rather than inheriting the previous message's, or a narrow
    // message rendered after a wide one would stay needlessly scaled.
    const container = Math.round(wrapRef.current?.clientWidth ?? 0);
    containerWidthRef.current = container;
    layoutWidthRef.current = container;
    widenPassesRef.current = 0;
    heightGrowthsRef.current = 0;
    appliedHeightRef.current = 0;
    if (container > 0) iframe.style.width = `${container}px`;
    iframe.style.transform = '';

    // Defer measurement to after layout to get accurate scroll metrics
    requestAnimationFrame(() => {
      measure();

      // Safety-net: re-measure after a short delay in case images/fonts
      // weren't ready during the first measurement
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(measure, 150);
    });

    // Watch for size changes (e.g., images loading, fonts rendering)
    bodyObserverRef.current?.disconnect();
    const bodyObserver = new ResizeObserver(() => {
      measure();
    });
    bodyObserver.observe(iframe.contentDocument.body);
    bodyObserverRef.current = bodyObserver;

    // Forward keyboard events from iframe to parent so global shortcuts work.
    // handleLoad runs on every (re)load; remove any prior listener first so a
    // single keypress isn't forwarded multiple times → double/triple archive.
    keydownCleanupRef.current?.();
    const doc = iframe.contentDocument;
    const forwardKeydown = (e: KeyboardEvent) => {
      // The forwarded event is re-dispatched on `window`, so its target becomes
      // window and the global handler's input-context guard can't see that the
      // user is typing inside a form field within this HTML email. Run the guard
      // here against the ORIGINAL in-iframe target and drop typing keystrokes so
      // a letter typed in an email <input> can't trigger a global archive/delete
      // (audit §2.17 #8).
      if (shouldIgnoreEvent(e)) {
        return;
      }
      const forwarded = new KeyboardEvent('keydown', {
        key: e.key,
        code: e.code,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
        bubbles: true,
        cancelable: true,
      });
      // dispatchEvent returns false when a listener called preventDefault() on
      // the CLONE. That cancellation cannot reach the real in-iframe event on its
      // own, so mirror it here: without this the original keydown stays
      // un-prevented and the browser still performs its text-insertion default
      // action — but by then the shortcut's handler has already rendered and
      // focused a field in the PARENT document (e.g. `r` opens the reply
      // composer and autofocuses To), so the keystroke lands there as a stray
      // character. Same class of bug for any single-key shortcut that focuses an
      // input.
      const notCancelled = window.dispatchEvent(forwarded);
      if (!notCancelled) {
        e.preventDefault();
      }
    };
    doc.addEventListener('keydown', forwardKeydown);
    keydownCleanupRef.current = () => {
      doc.removeEventListener('keydown', forwardKeydown);
      keydownCleanupRef.current = null;
    };
  }, [measure]);

  // Re-fit when the available width changes (rotation, split-pane resize,
  // sidebar toggle) — previously nothing watched the container at all, so a
  // rotated phone kept the portrait height and scale.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(wrap);
    wrapObserverRef.current = observer;
    return () => {
      observer.disconnect();
      wrapObserverRef.current = null;
    };
  }, [measure]);

  useEffect(() => {
    return () => {
      bodyObserverRef.current?.disconnect();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      keydownCleanupRef.current?.();
    };
  }, []);

  const extraStyles =
    [canvasStyles, dimmed ? 'body { opacity: 0.6; }' : ''].filter(Boolean).join('\n') || undefined;
  const srcdoc = useMemo(
    () => buildSrcdoc(styles, body, bodyAttributes, extraStyles),
    [styles, body, bodyAttributes, extraStyles],
  );

  return (
    <div className="w-full">
      {overflows && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFit}
            className="h-7 gap-1.5 px-2 text-[11px] text-muted-foreground"
          >
            {fitToWidth ? (
              <>
                <ZoomIn className="h-3 w-3" />
                Actual size
              </>
            ) : (
              <>
                <ZoomOut className="h-3 w-3" />
                Fit to width
              </>
            )}
          </Button>
        </div>
      )}
      <div
        ref={wrapRef}
        className="w-full overflow-x-auto overflow-y-hidden overscroll-x-contain"
      >
        <div ref={sizerRef} className="relative w-full">
          <iframe
            ref={iframeRef}
            srcDoc={srcdoc}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            onLoad={handleLoad}
            style={iframeStyle}
            title="Email content"
          />
        </div>
      </div>
    </div>
  );
}

export function MessageBody({
  sanitizedHtml,
  minHeight = DEFAULT_MIN_HEIGHT,
  contentWrapperClassName,
  expandQuotedByDefault = false,
}: MessageBodyProps) {
  const parsedContent = useMemo<ParsedEmailContent>(() => {
    if (!sanitizedHtml || (!sanitizedHtml.body.trim())) {
      return { mainContent: { styles: '', body: '' }, quotedContent: null, hasQuotedContent: false, mainIsEmpty: false };
    }
    return splitQuotedContent(sanitizedHtml);
  }, [sanitizedHtml]);

  // A reply sent with no new text is 100% quote — start expanded so the
  // message doesn't render blank behind a "Show quoted text" button.
  const defaultExpanded = expandQuotedByDefault || parsedContent.mainIsEmpty;
  const [showQuoted, setShowQuoted] = useState(defaultExpanded);

  // Re-baseline the toggle when the rendered message changes (the component
  // isn't always remounted per message), without clobbering the user's
  // toggle on unrelated re-renders.
  useEffect(() => {
    setShowQuoted(defaultExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedContent]);

  if (!sanitizedHtml || !sanitizedHtml.body.trim()) {
    return null;
  }

  return (
    <section className={cn(minHeight, 'space-y-3')}>
      <div className={cn(DEFAULT_CONTENT_WRAPPER, contentWrapperClassName)}>
        <EmailIframe
          styles={parsedContent.mainContent.styles}
          body={parsedContent.mainContent.body}
          bodyAttributes={sanitizedHtml.bodyAttributes}
          supportsDarkMode={sanitizedHtml.supportsDarkMode}
        />

        {parsedContent.hasQuotedContent && (
          <div className="mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuoted(!showQuoted)}
              className="gap-2 text-xs text-muted-foreground"
            >
              {showQuoted ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Hide quoted text
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Show quoted text
                </>
              )}
            </Button>

            {showQuoted && parsedContent.quotedContent && (
              <div className="mt-2 border-l-2 border-muted pl-4">
                <EmailIframe
                  styles={parsedContent.quotedContent.styles}
                  body={parsedContent.quotedContent.body}
                  bodyAttributes={sanitizedHtml.bodyAttributes}
                  supportsDarkMode={sanitizedHtml.supportsDarkMode}
                  dimmed
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
